/**
 * `<CssWater>` — water with no canvas at all.
 *
 * Two jobs. It is the **static tier**: where neither WebGL nor Canvas 2D is
 * available, `<WaterSurface>` draws this instead of nothing (D-5 — a missing
 * context is a downgrade, never a blank). And it is the **pure-CSS fallback for
 * Home's tide pools**, which may sit a dozen to a row where a graphics context
 * each would be absurd.
 *
 * A filled box and a crest line, from the same tokens the shader reads
 * (`--ae-water`, `--ae-crest`; flooded, `--ae-water-over` and `--ae-over`). It
 * eases to a new level with one CSS transition — about a second, inside D-6's
 * budget, and removed under `prefers-reduced-motion` by the global rule in
 * `index.css`. No transition runs at rest, so nothing moves on a clock.
 *
 * D-4: decoration. Hidden from assistive technology; the figure beside it is
 * the reading.
 */

import { cx } from "@/features/common/primitives";

export function CssWater({
  level,
  isOver = false,
  preview = null,
  className,
}: {
  /** 0..1; above 1 floods to the top. */
  level: number;
  isOver?: boolean;
  /** The rise-preview line, 0..1, or null for none. */
  preview?: number | null;
  className?: string;
}): React.JSX.Element {
  const fill = Math.max(0, Math.min(1, level));
  return (
    <div
      aria-hidden="true"
      data-over={isOver ? "true" : undefined}
      className={cx("relative overflow-hidden", className)}
      style={
        isOver
          ? ({ "--ae-crest": "var(--ae-over)", "--ae-water": "var(--ae-water-over)" } as React.CSSProperties)
          : undefined
      }
    >
      <div
        className="absolute inset-x-0 bottom-0 border-t-2 border-(--ae-crest) transition-[height] duration-1000 ease-out"
        style={{
          height: `${String(fill * 100)}%`,
          background:
            "linear-gradient(180deg, color-mix(in srgb, var(--ae-water) 42%, transparent), color-mix(in srgb, var(--ae-water) 80%, transparent))",
        }}
      />
      {preview === null ? null : (
        <div
          className="absolute inset-x-0 border-t border-dashed border-(--ae-crest) opacity-55 transition-[bottom] duration-300 ease-out"
          style={{ bottom: `${String(Math.max(0, Math.min(1, preview)) * 100)}%` }}
        />
      )}
    </div>
  );
}
